import React, { useMemo, useState } from 'react';
import { useGlobalData } from '../hooks/useGlobalHooks';
import { useRawPositionStore } from '../contexts/LTPStore/useRawPositionStore';
import { useWebSocketStatusStore } from '../contexts/LTPStore/useWebSocketStatusStore';
import WebSocketManager from '../contexts/websocketManager';
import { Clock } from 'lucide-react';

// ==========================================
// SHARED COMPONENT: PositionsTable
// ==========================================
// (Copied and simplified from RawPositionsView, removed exit actions as raw positions often aren't directly actionable via same strategy endpoints, or kept basic)

// ==========================================
// SHARED COMPONENT: PositionsTable
// ==========================================
// (Copied and simplified from RawPositionsView, removed exit actions as raw positions often aren't directly actionable via same strategy endpoints, or kept basic)

function getBrokerBadge(broker) {
    const brokerLower = broker?.toLowerCase() || "";

    if (brokerLower === "fyers") {
        return { text: "FY", class: "bg-blue-800 text-white" };
    } else if (brokerLower === "dhan_hq") {
        return { text: "DHQ", class: "bg-emerald-950 text-white" };
    } else if (brokerLower.includes("dhan")) {
        return { text: "DH", class: "bg-emerald-800 text-white" };
    } else if (brokerLower === "nubra") {
        return { text: "NU", class: "bg-purple-800 text-white" };
    } else if (brokerLower === "zerodha") {
        return { text: "ZE", class: "bg-[#EF5350] text-white" };
    } else if (brokerLower === "alpaca") {
        return { text: "AL", class: "bg-[#FCD72B] text-black" };
    }
    return { text: "??", class: "bg-gray-700 text-white" };
}

const PositionsTable = ({ data, loading, clientBrokerMap = null, clientData = null }) => {
    const SERIAL_KEY = "__row_no__";

    const clientNameMap = useMemo(() => {
        const map = {};
        if (clientData && Array.isArray(clientData)) {
            clientData.forEach(item => {
                map[item.clientId] = item.name || item.clientName || "";
            });
        }
        return map;
    }, [clientData]);

    const [searchQuery, setSearchQuery] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'last_updated', direction: 'desc' });
    const [filterSide, setFilterSide] = useState('ALL');

    // --- Helpers ---
    const formatDateTime = (isoString) => {
        if (!isoString || isoString === 0) return '-';

        try {
            // If the string doesn't have a timezone indicator (Z or +00:00), assume it's UTC and append 'Z'
            // This forces the Date constructor to treat it as UTC.
            let dateStr = String(isoString);
            if (!dateStr.match(/Z|[+-]\d{2}:?\d{2}$/)) {
                dateStr += 'Z';
            }

            const date = new Date(dateStr);

            return new Intl.DateTimeFormat('en-IN', {
                day: '2-digit',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false,
                timeZone: 'Asia/Kolkata'
            }).format(date);
        } catch {
            return String(isoString);
        }
    };


    const formatCurrency = (v) => {
        const num = parseFloat(v);
        if (Number.isNaN(num)) return String(v ?? '-');
        try {
            return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(num);
        } catch (e) { return num.toFixed(2); }
    };

    const normalizeSide = (s) => {
        if (s === 1 || s === '1' || String(s).toLowerCase().startsWith('b')) return 'BUY';
        if (s === 2 || s === -1 || s === '2' || String(s).toLowerCase().startsWith('s')) return 'SELL';
        return String(s || 'N/A').toUpperCase();
    };

    const prettifyKey = (k) => {
        if (!k) return '';
        if (k === SERIAL_KEY) return "No.";
        if (k === 'last_updated') return 'Time';
        const spaced = k.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/_/g, ' ');
        return spaced.split(' ').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
    };

    // --- Dynamic Columns ---
    const allKeys = useMemo(() => {
        const keys = new Set();
        // Explicitly exclude these keys as per user request
        const excludedKeys = new Set([
            "event", "strategy", "fytoken", "fy_token", "token", "lotsize", "lot_size", "segment",
            "position_source", "id", "entry", "last_order_dt",
            "exchangesegment", "producttype", "cfbuyqty", "cfsellqty", "symboltoken", "exchange_token"
        ]);

        if (data && data.length > 0) {
            data.forEach(p => {
                if (p && typeof p === "object") {
                    Object.keys(p).forEach(k => {
                        if (excludedKeys.has(k.toLowerCase())) return;
                        keys.add(k);
                    });
                }
            });
        }

        // Preferred Order: ClientID -> Symbol -> Buy Qty -> Sell Qty -> Net Qty -> Prices -> PnL -> Time
        const preferred = [
            "client_id", "clientId",
            "symbol",
            "side",
            "buy_qty", "sell_qty", "net_qty", "qty", // "make it like buy qty, sell qty then net qty"
            "buy_avg", "sell_avg", "net_avg", "avg_price",
            "ltp", "pl", "pnl",
            "last_updated"
        ];

        const ordered = [];
        preferred.forEach(k => {
            if (keys.has(k)) {
                ordered.push(k);
                keys.delete(k);
            }
        });

        return [SERIAL_KEY, ...ordered, ...Array.from(keys).sort()];
    }, [data]);

    // --- Filtering & Sorting ---
    const processed = useMemo(() => {
        if (!data || data.length === 0) return [];
        const q = searchQuery.trim().toLowerCase();

        let filtered = data.filter(p => {
            // Search
            if (q) {
                const clientId = p.client_id || p.clientId;
                const broker = clientBrokerMap ? (clientBrokerMap[clientId] || '') : '';
                const cName = clientNameMap[clientId] || '';
                const fields = [p.symbol, clientId, p.strategy, String(p.net_qty || p.qty || ''), String(p.pl || p.pnl || ''), broker, cName];
                if (!fields.some(f => String(f || '').toLowerCase().includes(q))) return false;
            }
            // Side
            const sideNormalized = normalizeSide(p.side);
            if (filterSide !== 'ALL' && sideNormalized !== filterSide) return false;
            return true;
        });

        // Sort
        if (sortConfig.key) {
            const { key, direction } = sortConfig;
            filtered.sort((a, b) => {
                const aRaw = a[key], bRaw = b[key];
                if (key === 'last_updated') {
                    const dateA = new Date(aRaw || 0).getTime();
                    const dateB = new Date(bRaw || 0).getTime();
                    return direction === 'asc' ? dateA - dateB : dateB - dateA;
                }
                const parse = (v) => {
                    const n = parseFloat(String(v).replace(/[^0-9.-]+/g, ''));
                    return Number.isFinite(n) ? n : NaN;
                };
                const aNum = parse(aRaw), bNum = parse(bRaw);
                if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
                    return direction === 'asc' ? aNum - bNum : bNum - aNum;
                }
                return String(aRaw || '').localeCompare(String(bRaw || ''));
            });
        }

        return filtered;
    }, [data, searchQuery, sortConfig, filterSide, clientBrokerMap]);

    // --- Stats ---
    const stats = useMemo(() => {
        const total = data ? data.length : 0;
        const clients = new Set();
        if (Array.isArray(data)) {
            data.forEach(p => {
                if (p.client_id) clients.add(p.client_id);
                if (p.clientId) clients.add(p.clientId);
            });
        }
        return {
            total,
            uniqueClients: clients.size
        };
    }, [data]);


    // --- Handlers ---
    const handleSort = (key) => {
        setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));
    };

    const getSortIcon = (key) => {
        if (["side", SERIAL_KEY].includes(key)) return null;
        if (sortConfig.key !== key) return (
            <svg className="w-3 h-3 text-gray-600 opacity-40" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
            </svg>
        );
        return sortConfig.direction === "asc" ? (
            <svg className="w-3 h-3 text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
        ) : (
            <svg className="w-3 h-3 text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
        );
    };




    if (loading) {
        return (
            <div className="bg-[#1a1d29] p-6 flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <svg className="animate-spin h-12 w-12 text-blue-500 inline-block" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                    <p className="text-gray-400 mt-4">Loading raw positions...</p>
                </div>
            </div>
        );
    }

    const isEmpty = !processed || processed.length === 0;


    return (
        <div className="flex flex-col h-full overflow-hidden bg-[#1a1d29]">
            {/* --- Controls Header --- */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 shrink-0 border-b border-gray-800">
                <div className="flex flex-col gap-3 w-full md:w-auto">
                    <div className="flex items-center gap-3">
                        <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search..." className="bg-[#0f1116] text-white text-sm px-4 py-2 rounded-md border border-gray-700 w-full md:w-80 focus:border-blue-500 focus:outline-none" />
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-400">
                        <span>Rows: {processed.length}</span>
                        <span>Clients: {stats.uniqueClients}</span>
                    </div>
                </div>


            </div>

            {/* --- Table Body --- */}
            <div className="flex-1 min-h-0 overflow-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-[#1a1d29]">
                <div className="inline-block min-w-full align-middle">
                    <table className="w-full text-sm text-left whitespace-nowrap">
                        <thead className="bg-[#14161f] sticky top-0 z-10 shadow-sm border-b border-gray-700/50">
                            <tr>
                                {allKeys.map(key => (
                                    <th key={key} onClick={() => handleSort(key)} className="py-3 px-4 text-gray-400 font-semibold text-xs uppercase tracking-wider cursor-pointer hover:bg-gray-800 transition-colors select-none">
                                        <div className="flex items-center gap-2"><span>{prettifyKey(key)}</span>{getSortIcon(key)}</div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800/50">
                            {isEmpty ? (
                                <tr>
                                    <td colSpan={Math.max(allKeys.length, 1)} className="py-20 text-center text-gray-500">
                                        <div className="flex flex-col items-center gap-2"><Clock className="w-8 h-8 opacity-20" /><span>No raw position data</span></div>
                                    </td>
                                </tr>
                            ) : (
                                processed.map((p, idx) => {
                                    const normalizedSide = normalizeSide(p.side);
                                    const isNetShort = p.net_qty < 0;
                                    const isSellHeavyRow = isNetShort || (parseFloat(p.sell_qty || 0) > parseFloat(p.buy_qty || 0));

                                    return (
                                        <tr key={idx} className={`transition-colors hover:bg-[#1f222e] ${isSellHeavyRow ? 'bg-[rgba(127,29,29,0.25)]' : ''}`}>
                                            {allKeys.map((k, colIndex) => {
                                                if (k === SERIAL_KEY) {
                                                    return (
                                                        <td key={k} className="py-3 px-4 text-gray-400 font-mono text-xs">{idx + 1}</td>
                                                    );
                                                }
                                                const raw = p[k];
                                                let cell = (raw === null || typeof raw === 'undefined') ? '-' : (typeof raw === 'object' ? JSON.stringify(raw) : String(raw));

                                                if ((k === "client_id" || k === "clientId") && clientBrokerMap) {
                                                    const broker = clientBrokerMap[raw] || "";
                                                    const cName = clientNameMap[raw] || "";
                                                    const badge = getBrokerBadge(broker);
                                                    return (
                                                        <td key={k} className="py-3 px-4 text-gray-300">
                                                            <div className="flex items-center gap-2">
                                                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${badge.class}`}>
                                                                    {badge.text}
                                                                </span>
                                                                <div className="flex items-baseline gap-1.5">
                                                                    {cName && <span className="text-gray-200 font-medium">{cName}</span>}
                                                                    <span className="text-xs text-gray-400 text-medium">{cName ? `(${cell})` : cell}</span>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    );
                                                }

                                                if (k === 'last_updated') return <td key={k} className="py-3 px-4 text-gray-400 text-xs font-mono">{formatDateTime(raw)}</td>;
                                                if (k === 'side') {
                                                    const isBuy = normalizedSide === 'BUY';
                                                    return <td key={k} className="py-3 px-4"><span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border ${isBuy ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}`}>{normalizedSide}</span></td>;
                                                }
                                                if (['pl', 'pnl', 'realized_pl', 'unrealized_pl'].includes(k.toLowerCase())) {
                                                    const num = parseFloat(raw) || 0;
                                                    return <td key={k} className={`py-3 px-4 font-mono font-bold ${num >= 0 ? 'text-green-400' : 'text-red-400'}`}>{formatCurrency(num)}</td>;
                                                }

                                                // Check if it's a numeric field but NOT a quantity field
                                                const isQtyField = ['net_qty', 'buy_qty', 'sell_qty', 'qty', 'cfbuyqty', 'cfsellqty'].includes(k.toLowerCase());
                                                const numericValue = parseFloat(raw);

                                                // If it's a number (and not ID or boolean)
                                                if (!isNaN(numericValue) && typeof raw !== 'boolean' && k !== 'client_id' && k !== 'clientId') {
                                                    if (isQtyField) {
                                                        // Quantities: Show as is (integers)
                                                        return <td key={k} className="py-3 px-4 text-gray-300 font-mono text-xs">{raw}</td>;
                                                    } else {
                                                        // Prices/Other Values: Force 2 decimals
                                                        return <td key={k} className="py-3 px-4 text-gray-300 font-mono text-xs">{numericValue.toFixed(2)}</td>;
                                                    }
                                                }

                                                return <td key={k} className="py-3 px-4 text-gray-300">{cell}</td>;
                                            })}
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export function BrokerRawPositionsView({ clientData }) {
    const rawPositions = useRawPositionStore((state) => state.rawPositions);
    const [activeTab, setActiveTab] = useState('ALL');
    const [selectedBroker, setSelectedBroker] = useState('ALL');
    const [statsFilter, setStatsFilter] = useState('ALL'); // 'ALL' | 'INDEX'

    const clientBrokerMap = useMemo(() => {
        const map = {};
        if (clientData && Array.isArray(clientData)) {
            clientData.forEach(item => {
                map[item.clientId] = item.broker;
            });
        }
        return map;
    }, [clientData]);

    const availableBrokers = useMemo(() => {
        const brokers = new Set(['ALL']);
        if (clientData) {
            clientData.forEach(c => {
                if (c.broker) brokers.add(c.broker);
            });
        }
        return Array.from(brokers);
    }, [clientData]);

    const filteredPositions = useMemo(() => {
        if (!rawPositions) return [];
        if (activeTab === 'ALL') return rawPositions;

        return rawPositions.filter(p => {
            const qty = parseFloat(p.net_qty ?? p.qty ?? 0);
            if (activeTab === 'OPEN') return qty !== 0;
            if (activeTab === 'CLOSED') return qty === 0;
            return true;
        });
    }, [rawPositions, activeTab]);

    const finalFilteredPositions = useMemo(() => {
        let result = filteredPositions;

        // Filter by Broker
        if (selectedBroker !== 'ALL') {
            result = result.filter(p => {
                const b = clientBrokerMap[p.client_id || p.clientId];
                return b === selectedBroker;
            });
        }

        // Filter by Index (Stats Filter)
        if (statsFilter === 'INDEX') {
            result = result.filter(p => {
                const symbol = (p.symbol || '').toUpperCase();
                return symbol.includes('NIFTY') || symbol.includes('BANKNIFTY') ||
                    symbol.includes('FINNIFTY') || symbol.includes('SENSEX') ||
                    symbol.includes('MIDCPNIFTY');
            });
        }

        return result;
    }, [filteredPositions, selectedBroker, clientBrokerMap, statsFilter]);

    const tabClasses = (tabName) => `px-6 py-3 text-sm font-medium border-b-2 transition-colors duration-200 ${activeTab === tabName
        ? 'border-blue-500 text-blue-400 bg-blue-500/10'
        : 'border-transparent text-gray-400 hover:text-gray-200 hover:bg-white/5'
        }`;

    // Calculate Total Buy and Total Sell using buy_qty and sell_qty
    const stats = useMemo(() => {
        let totalBuy = 0;
        let totalSell = 0;

        finalFilteredPositions.forEach(p => {
            const bQty = parseFloat(p.buy_qty || 0);
            const sQty = parseFloat(p.sell_qty || 0);

            totalBuy += bQty;
            totalSell += sQty;
        });

        return { totalBuy, totalSell };
    }, [finalFilteredPositions]);

    const isSellHeavy = stats.totalSell > stats.totalBuy;

    return (
        <div className="flex flex-col h-full bg-[#1a1d29]">
            {/* Tab Navigation */}
            <div className="flex justify-between items-center border-b border-gray-700 bg-[#14161f] pr-6">
                <div className="flex">
                    <button onClick={() => setActiveTab('ALL')} className={tabClasses('ALL')}>All Positions</button>
                    <button onClick={() => setActiveTab('OPEN')} className={tabClasses('OPEN')}>Open Positions</button>
                    <button onClick={() => setActiveTab('CLOSED')} className={tabClasses('CLOSED')}>Closed Positions</button>
                </div>

                {/* Broker Filter Chips */}


                {/* Stats Header */}
                <div className="flex items-center gap-6">
                    {/* Filter Toggle */}
                    <div className="flex bg-gray-800 rounded p-0.5">
                        <button
                            onClick={() => setStatsFilter('ALL')}
                            className={`px-3 py-1 text-xs font-medium rounded transition-colors ${statsFilter === 'ALL' ? 'bg-gray-600 text-white' : 'text-gray-400 hover:text-white'}`}
                        >
                            All
                        </button>
                        <button
                            onClick={() => setStatsFilter('INDEX')}
                            className={`px-3 py-1 text-xs font-medium rounded transition-colors ${statsFilter === 'INDEX' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`}
                        >
                            Index Only
                        </button>
                    </div>

                    <div className={`flex items-center gap-6 text-sm font-medium ${isSellHeavy ? 'text-red-400' : 'text-gray-300'}`}>
                        <div className="flex items-center gap-2">
                            <span className="text-gray-500 uppercase text-xs tracking-wider">Total Buy</span>
                            <span className="font-mono font-bold text-green-400">{stats.totalBuy}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-gray-500 uppercase text-xs tracking-wider">Total Sell</span>
                            <span className="font-mono font-bold text-red-400">{stats.totalSell}</span>
                        </div>
                    </div>
                </div>

            </div>
            <div className="flex items-center gap-2 my-2 pl-3">

                {availableBrokers.map(b => (
                    <button
                        key={b}
                        onClick={() => setSelectedBroker(b)}
                        className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${selectedBroker === b
                            ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30'
                            : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                            }`}
                    >
                        {b === 'ALL' ? 'All Brokers' : b.toUpperCase()}
                    </button>
                ))}
            </div>

            <div className="flex-1 min-h-0">
                <PositionsTable
                    data={finalFilteredPositions}
                    loading={false}
                    clientBrokerMap={clientBrokerMap}
                    clientData={clientData}
                />
            </div>
        </div >
    );
}
